import type { HealItem, ItemId } from '../../shared/items.ts';
import { itemCatalog } from '../common/item-catalog.ts';
import type { Inventory, OwnedMonsterView, TeamAction } from '../../shared/protocol.ts';
import { confirmDialog } from '../common/confirm-dialog.ts';
import { $ } from '../common/dom.ts';
import { icon } from '../common/icons.ts';
import { monsterCard, type CardAction, type OpenMonster } from './monster-card.ts';
import { creatureText } from '../../shared/game-config.ts';

export interface TeamPanelHandlers {
  onAction(action: TeamAction): void;
  onHeal(item: ItemId, monsterId: number): void;
  /** Ouvre la fiche détaillée du monstre. */
  onOpen: OpenMonster;
}

/**
 * Boutons haut / bas pour déplacer un monstre dans l'équipe (utilisables au doigt comme à la souris) ; sur la carte
 * seulement, la fiche ayant ses flèches vers les monstres voisins.
 */
export function reorderActions(index: number, count: number, moveTo: (to: number) => void): CardAction[] {
  return [
    ...(index > 0 ? [{ label: '', icon: 'chevron-up' as const, title: 'Monter', onClick: () => moveTo(index - 1), variant: 'secondary' as const, cardOnly: true }] : []),
    ...(index < count - 1 ? [{ label: '', icon: 'chevron-down' as const, title: 'Descendre', onClick: () => moveTo(index + 1), variant: 'secondary' as const, cardOnly: true }] : []),
  ];
}

/** Glisser-déposer d'une carte sur une autre de la même liste : le monstre prend sa place. */
function makeDraggable(card: HTMLElement, list: HTMLElement, id: number, index: number, move: (id: number, to: number) => void): void {
  card.draggable = true;
  card.dataset.index = String(index);
  // Appui long au doigt : pas de menu du navigateur (ouvrir l'image…), seulement le glisser.
  card.addEventListener('contextmenu', (event) => event.preventDefault());
  card.addEventListener('dragstart', (event) => {
    event.dataTransfer?.setData('text/plain', String(id));
    list.dataset.dragging = String(id);
    card.classList.add('dragging');
  });
  card.addEventListener('dragend', () => {
    delete list.dataset.dragging;
    card.classList.remove('dragging');
    for (const other of list.children) other.classList.remove('drop-target');
  });
  card.addEventListener('dragover', (event) => {
    if (!list.dataset.dragging || list.dataset.dragging === String(id)) return;
    event.preventDefault();
    card.classList.add('drop-target');
  });
  card.addEventListener('dragleave', () => card.classList.remove('drop-target'));
  card.addEventListener('drop', (event) => {
    event.preventDefault();
    const dragged = Number(list.dataset.dragging);
    card.classList.remove('drop-target');
    if (dragged && dragged !== id) move(dragged, index);
  });
}

/** La plus petite potion suffisante disponible, sinon la plus forte possédée. */
export function pickPotion(missingHp: number, items: Inventory['items']): HealItem | null {
  const owned = itemCatalog.heals().filter((item) => (items[item.id] ?? 0) > 0);
  return owned.find((item) => item.effect.amount >= missingHp) ?? owned.at(-1) ?? null;
}

/** Panneau latéral : l'équipe (ordre, surnoms, soins). La boîte se gère devant un PC. */
export class TeamPanel {
  private readonly panel = $('#team-panel');
  private readonly teamList = $('#team-list');
  private readonly boxInfo = $('#box-info');
  private readonly handlers: TeamPanelHandlers;
  private collection: OwnedMonsterView[] = [];
  private inventory: Inventory | null = null;

  constructor(handlers: TeamPanelHandlers) {
    this.handlers = handlers;
    $('#btn-team').addEventListener('click', () => this.toggle());
    $('#team-close').addEventListener('click', () => this.close());
  }

  get isOpen(): boolean {
    return !this.panel.hidden;
  }

  toggle(): void {
    this.panel.hidden = !this.panel.hidden;
  }

  close(): void {
    this.panel.hidden = true;
  }

  setInventory(inventory: Inventory): void {
    this.inventory = inventory;
    this.render(this.collection);
  }

  render(collection: OwnedMonsterView[]): void {
    this.collection = collection;
    const team = collection.filter((monster) => monster.slot !== null);
    const boxed = collection.length - team.length;
    const rename = (id: number) => (nickname: string) => this.handlers.onAction({ action: 'rename', id, nickname });
    const move = (id: number, to: number) => this.handlers.onAction({ action: 'move', id, to });

    this.teamList.replaceChildren(...team.map((monster, index) => {
      const card = monsterCard(monster, this.cardActions(monster, index, team.length), rename(monster.id), (opened) => this.openSheet(opened.id));
      makeDraggable(card, this.teamList, monster.id, index, move);
      return card;
    }));
    this.boxInfo.replaceChildren(icon('box'), boxed
      ? creatureText(` ${boxed} ${boxed > 1 ? '{monstres}' : '{monstre}'} dans la boîte : utilisez un PC de stockage pour échanger.`)
      : creatureText(' Boîte vide. Les {monstres} capturé{·e}s quand l\'équipe est pleine y sont envoyé{·e}s (PC de stockage).'));
  }

  private cardActions(monster: OwnedMonsterView, index: number, count: number): CardAction[] {
    return [
      ...this.healAction(monster),
      ...reorderActions(index, count, (to) => this.handlers.onAction({ action: 'move', id: monster.id, to })),
    ];
  }

  /** Ouvre la fiche d'un monstre de l'équipe, avec des flèches vers ses voisins (la liste boucle). */
  private openSheet(id: number): void {
    const team = this.collection.filter((monster) => monster.slot !== null);
    const index = team.findIndex((monster) => monster.id === id);
    const monster = team[index];
    if (!monster) return;
    const neighbour = (step: number) => () => {
      const next = team[(index + step + team.length) % team.length];
      if (next) this.openSheet(next.id);
    };
    this.handlers.onOpen(monster, [...this.cardActions(monster, index, team.length), ...this.releaseAction(monster, team.length)], team.length > 1
      ? { previous: neighbour(-1), next: neighbour(1), position: `${index + 1} / ${team.length}` }
      : undefined);
  }

  /** Relâcher : dans la fiche seulement, pour ne pas surcharger la carte ; le dernier monstre de l'équipe reste. */
  private releaseAction(monster: OwnedMonsterView, teamSize: number): CardAction[] {
    if (teamSize <= 1) return [];
    return [{
      label: 'Relâcher',
      variant: 'danger',
      onClick: async () => {
        const confirmed = await confirmDialog({ title: `Relâcher ${monster.name} ?`, message: 'Il retournera à la vie sauvage.', icon: 'flee', confirmLabel: 'Relâcher', danger: true });
        if (confirmed) this.handlers.onAction({ action: 'release', id: monster.id });
      },
    }];
  }

  private healAction(monster: OwnedMonsterView): CardAction[] {
    const missing = monster.maxHp - monster.hp;
    if (missing <= 0 || monster.hp <= 0 || !this.inventory) return [];
    const potion = pickPotion(missing, this.inventory.items);
    if (!potion) return [];
    return [{ label: 'Soigner', title: potion.name, icon: potion.image ? 'heal' : potion.icon, iconColor: potion.image ? undefined : potion.color, onClick: () => this.handlers.onHeal(potion.id, monster.id), variant: 'secondary', cardOnly: true }];
  }
}
